import PropTypes from 'prop-types';
import React from 'react';

import { CircleLoader } from 'common-src/components/base';
import { AthenaDepartment } from 'common-src/models/PatientIntegrationsAthena';
import { ColorsNew } from 'common-src/styles';

import { VISIT_TYPES } from '../../constants';
import { dayName, daysUntil, getAppointmentReadinessStatus, monthDay } from '../../helpers';
import AppointmentCard from '../AppointmentCard';
import ReadinessDetail from '../ReadinessDetail';
import TaskDetail from '../TaskDetail';
import styles from './ReadinessView.module.scss';

const ALL_DEPARTMENTS_KEY = 'all';

const ReadinessView = ({
  isLoading,
  apptsByDate,
  selectedAppt,
  taskFromReadiness,
  appointmentTasksById,
  setSelectedAppt,
  setTaskFromReadiness,
  onMarkComplete,
  markingTaskId,
  onReassignTask,
  reassigningTaskId,
  onAssignToMe,
  assigningTaskId,
  onUnassignTask,
  unassigningTaskId,
  currentUserName,
  onReplySent,
}) => {
  const [selectedDepartment, setSelectedDepartment] = React.useState(ALL_DEPARTMENTS_KEY);
  const hasApptsByDateProp = apptsByDate != null;
  const safeApptsByDate = apptsByDate ?? {};
  const flattenedAppointments = React.useMemo(
    () => Object.values(safeApptsByDate).flat(),
    [safeApptsByDate],
  );
  const departmentDefinitions = React.useMemo(
    () =>
      Object.values(AthenaDepartment)
        .filter((department) => department.id !== AthenaDepartment.TestDepartment.id)
        .map((department) => ({
          key: `${department.id}`,
          label: department.value,
        })),
    [],
  );
  const departmentLabelsById = React.useMemo(
    () =>
      departmentDefinitions.reduce(
        (acc, department) => ({ ...acc, [department.key]: department.label }),
        {},
      ),
    [departmentDefinitions],
  );
  const departmentOptions = React.useMemo(
    () => [{ key: ALL_DEPARTMENTS_KEY, label: 'All clinics' }, ...departmentDefinitions],
    [departmentDefinitions],
  );
  const departmentReadinessById = React.useMemo(
    () =>
      departmentDefinitions.reduce((acc, department) => {
        const departmentAppointments = flattenedAppointments.filter(
          (appointment) => `${appointment?.departmentAthenaId ?? ''}`.trim() === department.key,
        );

        if (!departmentAppointments.length) {
          acc[department.key] = 'default';
          return acc;
        }

        const hasRed = departmentAppointments.some(
          (appointment) =>
            getAppointmentReadinessStatus(appointmentTasksById[appointment.id] || []) === 'red',
        );
        const hasYellow = departmentAppointments.some(
          (appointment) =>
            getAppointmentReadinessStatus(appointmentTasksById[appointment.id] || []) === 'yellow',
        );

        acc[department.key] = hasRed ? 'red' : hasYellow ? 'yellow' : 'green';
        return acc;
      }, {}),
    [appointmentTasksById, departmentDefinitions, flattenedAppointments],
  );
  const filteredAppointments = React.useMemo(() => {
    if (selectedDepartment === ALL_DEPARTMENTS_KEY) return flattenedAppointments;

    return flattenedAppointments.filter(
      (appointment) => `${appointment?.departmentAthenaId ?? ''}`.trim() === selectedDepartment,
    );
  }, [flattenedAppointments, selectedDepartment]);
  const filteredApptsByDate = React.useMemo(
    () =>
      filteredAppointments.reduce((acc, appointment) => {
        const dateKey = `${appointment?.date ?? ''}`.trim();
        if (!dateKey) return acc;
        acc[dateKey] = acc[dateKey] || [];
        acc[dateKey].push(appointment);
        return acc;
      }, {}),
    [filteredAppointments],
  );
  const filteredReadinessStats = React.useMemo(
    () =>
      filteredAppointments.reduce(
        (acc, appointment) => {
          const readinessStatus = getAppointmentReadinessStatus(
            appointmentTasksById[appointment.id] || [],
          );

          if (readinessStatus === 'green') acc.green += 1;
          if (readinessStatus === 'yellow') acc.yellow += 1;
          if (readinessStatus === 'red') acc.red += 1;

          return acc;
        },
        { red: 0, yellow: 0, green: 0 },
      ),
    [appointmentTasksById, filteredAppointments],
  );
  const next14DayAppointments = hasApptsByDateProp
    ? filteredAppointments.filter((appointment) => {
        const dayOffset = appointment?.date ? daysUntil(appointment.date) : null;
        return Number.isFinite(dayOffset) && dayOffset >= 0 && dayOffset <= 14;
      }).length
    : 0;

  React.useEffect(() => {
    const filteredAppointmentIds = new Set(
      filteredAppointments.map((appointment) => `${appointment?.id || ''}`),
    );

    if (selectedAppt?.id && !filteredAppointmentIds.has(`${selectedAppt.id}`)) {
      setSelectedAppt(null);
      setTaskFromReadiness(null);
      return;
    }

    if (
      taskFromReadiness?.externalObjectId &&
      !filteredAppointmentIds.has(`${taskFromReadiness.externalObjectId}`)
    ) {
      setTaskFromReadiness(null);
    }
  }, [
    filteredAppointments,
    selectedAppt,
    setSelectedAppt,
    setTaskFromReadiness,
    taskFromReadiness,
  ]);

  const sortedDates = Object.keys(filteredApptsByDate).sort((a, b) => {
    const aTime = new Date(a).getTime();
    const bTime = new Date(b).getTime();
    const aValid = Number.isFinite(aTime);
    const bValid = Number.isFinite(bTime);
    if (aValid && bValid) return aTime - bTime;
    if (aValid) return -1;
    if (bValid) return 1;
    return a.localeCompare(b);
  });

  const stats = [
    { key: 'upcoming', label: 'Next 14 days', value: next14DayAppointments, sub: 'visits' },
    { key: 'green', label: 'Ready', value: filteredReadinessStats.green, tone: 'green' },
    { key: 'yellow', label: 'At Risk', value: filteredReadinessStats.yellow, tone: 'yellow' },
    { key: 'red', label: 'Not Ready', value: filteredReadinessStats.red, tone: 'red' },
  ];
  const readinessAppointmentLabel = selectedAppt
    ? `${selectedAppt?.patient?.name || 'Appointment'} · ${
        VISIT_TYPES[selectedAppt?.visitType]?.label || selectedAppt?.visitType || ''
      }`
    : '';
  const readinessStatus = selectedAppt
    ? getAppointmentReadinessStatus(appointmentTasksById[selectedAppt?.id] || [])
    : 'default';

  return (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        <div className={styles.departmentTabs} role="tablist" aria-label="Department filters">
          {departmentOptions.map((department) => (
            <button
              key={department.key}
              type="button"
              role="tab"
              aria-selected={selectedDepartment === department.key}
              onClick={() => {
                setSelectedDepartment(department.key);
              }}
              className={`${styles.departmentTab} ${
                selectedDepartment === department.key ? styles.departmentTabActive : ''
              }`}
              data-tone={departmentReadinessById[department.key] || 'default'}
            >
              {department.label}
            </button>
          ))}
        </div>
        <div
          className={styles.toolbarMeta}
        >{`${next14DayAppointments} appointments · next 14 days`}</div>
      </div>

      <div className={styles.statsRow}>
        {stats.map((stat) => (
          <div key={stat.key} className={styles.statCard}>
            <div className={styles.statLabel}>{stat.label}</div>
            <div className={styles.statValueRow}>
              <span
                className={`${styles.statValue} ${
                  stat.tone ? styles[`statValue${stat.tone}`] : ''
                }`}
              >
                {stat.value}
              </span>
              {stat.sub && <span className={styles.statSub}>{stat.sub}</span>}
            </div>
          </div>
        ))}
      </div>

      <div
        className={`${styles.layout} ${
          selectedAppt || taskFromReadiness ? styles.layoutWithDetail : ''
        }`}
      >
        <div className={styles.listColumn}>
          {isLoading && (
            <div className={styles.loaderWrap}>
              <CircleLoader color={ColorsNew.darkGreen} strokeWidth={2} circleRadius={12} />
            </div>
          )}
          {!isLoading && !sortedDates.length && (
            <div className={styles.emptyFilterState}>
              {selectedDepartment === ALL_DEPARTMENTS_KEY
                ? 'No appointments available.'
                : `No appointments for ${departmentLabelsById[selectedDepartment] || 'this department'}.`}
            </div>
          )}
          {sortedDates.map((date) => {
            const appointments = filteredApptsByDate[date] || [];
            const dateObj = new Date(date);
            const isValidDate = Number.isFinite(dateObj.getTime());
            const dayOffset = isValidDate ? daysUntil(date) : null;
            const isToday = dayOffset === 0;
            const dayLabel = isValidDate ? dayName(date) : '—';
            const dayNumber = isValidDate ? dateObj.getDate() : '—';
            const monthLabel = isValidDate ? monthDay(date) : '—';

            return (
              <div key={date} className={styles.dateGroup}>
                <div className={styles.dateHeader}>
                  <div className={styles.dateLeft}>
                    <div
                      className={`${styles.dateDayName} ${
                        isToday ? styles.dateToday : styles.dateMuted
                      }`}
                    >
                      {dayLabel}
                    </div>
                    <div
                      className={`${styles.dateDayNumber} ${
                        isToday ? styles.dateToday : styles.dateNormal
                      }`}
                    >
                      {dayNumber}
                    </div>
                  </div>
                  <div className={styles.dateDivider} />
                  <span className={styles.dateMeta}>
                    {monthLabel}
                    {isToday ? ' · Today' : ''}
                  </span>
                </div>
                <div className={styles.cardsGrid}>
                  {appointments.map((appt) => (
                    <AppointmentCard
                      key={appt.id}
                      appt={appt}
                      isSelected={selectedAppt?.id === appt.id}
                      appointmentTasks={appointmentTasksById[appt.id] || []}
                      onClick={() => {
                        setSelectedAppt(appt);
                        setTaskFromReadiness(null);
                      }}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        {(selectedAppt || taskFromReadiness) && (
          <div className={styles.detailPanel}>
            {taskFromReadiness ? (
              <TaskDetail
                task={taskFromReadiness}
                onClose={() => setTaskFromReadiness(null)}
                onBackToReadiness={() => setTaskFromReadiness(null)}
                readinessAppointmentLabel={readinessAppointmentLabel}
                readinessStatus={readinessStatus}
                onAssignToMe={onAssignToMe}
                isAssigning={assigningTaskId === taskFromReadiness.id}
                onUnassign={onUnassignTask}
                isUnassigning={unassigningTaskId === taskFromReadiness.id}
                onMarkComplete={onMarkComplete}
                isMarking={markingTaskId === taskFromReadiness.id}
                onReassign={onReassignTask}
                isReassigning={reassigningTaskId === taskFromReadiness.id}
                currentUserName={currentUserName}
                onReplySent={onReplySent}
              />
            ) : (
              <ReadinessDetail
                appt={selectedAppt}
                onClose={() => setSelectedAppt(null)}
                onSelectTask={setTaskFromReadiness}
                appointmentTasks={appointmentTasksById[selectedAppt?.id] || []}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

ReadinessView.propTypes = {
  isLoading: PropTypes.bool,
  apptsByDate: PropTypes.objectOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      }),
    ),
  ),
  selectedAppt: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    patient: PropTypes.shape({
      name: PropTypes.string,
    }),
    visitType: PropTypes.string,
  }),
  taskFromReadiness: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    externalObjectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  appointmentTasksById: PropTypes.object,
  setSelectedAppt: PropTypes.func,
  setTaskFromReadiness: PropTypes.func,
  onMarkComplete: PropTypes.func,
  markingTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onReassignTask: PropTypes.func,
  reassigningTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onAssignToMe: PropTypes.func,
  assigningTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onUnassignTask: PropTypes.func,
  unassigningTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currentUserName: PropTypes.string,
  onReplySent: PropTypes.func,
};

export default ReadinessView;
